// =============================================================================
//  L'ÉCRAN D'ENVOI — destinataires, annuaire, texte complémentaire
// =============================================================================
//  🔴 CE QU'IL RÉPARE. La capacité existait côté serveur depuis `9faeb62eb`
//  et n'était atteignable QUE par l'API : pour choisir un CC, il fallait un
//  agent et un curl. DEC-025 dit que le choix des destinataires reste HUMAIN —
//  l'humain ne pouvait pas l'exercer. C'est DEC-012 : « toute intervention
//  technique nécessaire pour faire partir un envoi est un défaut
//  d'industrialisation ».
//
//  🔑 LE CORPS AFFICHÉ EST LE CORPS RECOMPOSÉ. Chaque modification passe par
//  `PATCH /brouillons/{id}`, qui rappelle `composer_corps()` — la même fonction
//  que la préparation — et rend le brouillon à jour. ⛔ Rien n'est recomposé
//  ici : l'écran AFFICHE, il ne fabrique pas. C'est ce qui rend impossible que
//  le canary et le pli réel diffèrent d'un caractère.
//
//  ⚠️ Composant AUTONOME, inséré par UNE ligne dans `HabilitationANAH.jsx`
//  (1698 lignes, en exploitation) : le fichier n'est ouvert qu'à un endroit.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, X, BookUser, UserPlus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const BASE = `${API}/api/metier/habilitation`;

const CHAMPS = [
  { cle: "to", libelle: "À", aide: "au moins un destinataire" },
  { cle: "cc", libelle: "Copie (CC)", aide: "visible de tous" },
  { cle: "bcc", libelle: "Copie invisible (CCI)", aide: "⛔ n'apparaît dans aucun en-tête" },
];

const TYPE_STYLE = {
  generique: { bg: "var(--oh-stat-blue-bg)", text: "var(--oh-stat-blue-text)", label: "service" },
  nominatif: { bg: "var(--oh-stat-purple-bg)", text: "var(--oh-stat-purple-text)", label: "agent" },
};

// 🔴 LA RÈGLE DE POSE — UNE SEULE, réutilisée par le bouton « + », par la touche
// Entrée ET par « Enregistrer ». ⛔ Deux implémentations de cette règle
// divergeraient, et c'est exactement la divergence qui a produit l'incident du
// 2026-09-17 : l'adresse était POSÉE sur un chemin et PERDUE sur l'autre.
// ⚠️ Une adresse ne vit que dans UNE liste : présente deux fois, elle recevrait
// deux exemplaires — on la retire des autres plutôt que de laisser le serveur
// refuser l'envoi entier.
export function poser(adresses, cle, valeur) {
  const propre = String(valeur || "").trim().toLowerCase();
  if (!propre) return adresses;
  const suivant = { to: [...adresses.to], cc: [...adresses.cc], bcc: [...adresses.bcc] };
  CHAMPS.forEach(({ cle: k }) => {
    suivant[k] = suivant[k].filter((x) => x.toLowerCase() !== propre);
  });
  suivant[cle] = [...suivant[cle], propre];
  return suivant;
}

// 🔴 CE QUE L'OPÉRATEUR VOIT FAIT PARTIE DE CE QU'IL ENVOIE.
//
// ⚠️ INCIDENT PROD MESURÉ LE 2026-09-17 : JJ a saisi l'adresse de l'ANAH 93,
// l'a VUE à l'écran, et l'application a répondu « envoi refusé : aucun
// destinataire ». Cause : le texte tapé vivait dans `saisie` et n'entrait dans
// `adresses` QUE via le bouton « + » ou la touche Entrée. Cliquer directement
// sur « Enregistrer » envoyait donc `{to: []}` — un vide que l'opérateur ⛔ ne
// pouvait pas comprendre, puisque son adresse était sous ses yeux.
// (Mesuré en base : le brouillon portait `destinataire_email = None` et
// `modifie_le = None` — la requête partait bel et bien, vide, et le serveur la
// refusait correctement.)
//
// 🔑 LE CONTRAT : le payload dit L'ÉCRAN. ⛔ Il ⛔ ne peut PAS partir sans une
// adresse que l'opérateur voit. La garde serveur ⛔ n'est PAS contournée — elle
// reçoit enfin ce qui lui était destiné.
export function adressesEffectives(adresses, saisie) {
  return CHAMPS.reduce((acc, { cle }) => poser(acc, cle, (saisie || {})[cle]), adresses);
}

const etiquette = { fontSize: 12, color: "var(--oh-text-secondary)", fontWeight: 500 };
const champStyle = {
  height: 34, borderRadius: 8, padding: "0 10px", fontSize: 13, width: "100%",
  background: "var(--oh-bg-elevated)", color: "var(--oh-text)",
  border: "0.5px solid var(--oh-border)",
};

export default function DestinatairesPanel({ brouillon, onBrouillonChange, onErreur }) {
  const [adresses, setAdresses] = useState({ to: [], cc: [], bcc: [] });
  const [textes, setTextes] = useState({});
  const [saisie, setSaisie] = useState({ to: "", cc: "", bcc: "" });
  const [fiches, setFiches] = useState([]);
  const [proposition, setProposition] = useState(null);
  const [occupe, setOccupe] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [nouvelle, setNouvelle] = useState(null);

  const demandes = useMemo(
    () => brouillon?.perimetre_demandes || [], [brouillon]);

  // ⚠️ Les départements du pli — c'est sur eux que l'annuaire propose.
  const departements = useMemo(() => {
    const vus = new Set();
    demandes.forEach((d) => (d.territoires || []).forEach((t) => vus.add(String(t))));
    (brouillon?.territoire || []).forEach((t) => vus.add(String(t)));
    return [...vus];
  }, [demandes, brouillon]);

  // ── L'ÉTAT VIENT DU BROUILLON, ⛔ jamais d'un état local qui dériverait ──
  useEffect(() => {
    if (!brouillon) return;
    const d = brouillon.destinataires || {};
    setAdresses({
      to: d.to || (brouillon.destinataire_email ? [brouillon.destinataire_email] : []),
      cc: d.cc || [],
      bcc: d.bcc || [],
    });
    const t = {};
    (brouillon.perimetre_demandes || []).forEach((dem) => {
      t[dem.code] = dem.texte_complementaire || "";
    });
    setTextes(t);
  }, [brouillon]);

  const charger = useCallback(async () => {
    if (!departements.length) return;
    try {
      const params = new URLSearchParams({ departements: departements.join(",") });
      if (brouillon?.objet_pli) params.set("objet_pli", brouillon.objet_pli);
      // ⚠️ L'annuaire est chargé SANS filtre de département. Filtrer sur
      // `departements[0]` masquerait les fiches du SECOND territoire sur un pli
      // qui en couvre deux — l'opérateur ne pourrait pas choisir ce qu'il ne
      // voit pas. C'est la proposition qui porte le ciblage, ⛔ pas la liste.
      const [rf, rp] = await Promise.all([
        fetch(`${BASE}/annuaire`, { credentials: "include" }),
        fetch(`${BASE}/annuaire/proposition?${params}`, { credentials: "include" }),
      ]);
      if (rf.ok) setFiches((await rf.json()).fiches || []);
      if (rp.ok) setProposition(await rp.json());
    } catch { /* l'annuaire reste simplement indisponible */ }
  }, [departements, brouillon]);

  useEffect(() => { charger(); }, [charger]);

  const ajouter = (cle, valeur) => {
    if (!String(valeur || "").trim()) return;
    setAdresses((a) => poser(a, cle, valeur));
    setSaisie((s) => ({ ...s, [cle]: "" }));
  };

  const retirer = (cle, valeur) =>
    setAdresses((a) => ({ ...a, [cle]: a[cle].filter((x) => x !== valeur) }));

  const enregistrer = useCallback(async () => {
    setOccupe("enregistrer"); setErreur(null);
    // 🔴 On POSE d'abord ce qui est saisi mais pas encore ajouté : l'opérateur
    // qui tape une adresse puis clique « Enregistrer » ⛔ ne doit PAS se voir
    // répondre « aucun destinataire » alors qu'elle est sous ses yeux.
    const effectives = adressesEffectives(adresses, saisie);
    try {
      const r = await fetch(`${BASE}/brouillons/${brouillon.id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ destinataires: effectives, textes }),
      });
      const corps = await r.json();
      if (!r.ok) {
        // ⚠️ Le motif EXACT du refus. ⛔ « Erreur » n'apprend rien à l'opérateur.
        setErreur(corps.detail?.motif || corps.detail || `Refus (${r.status})`);
        return;
      }
      // 🔑 Le brouillon RENDU par le serveur remplace le nôtre : le corps
      // affiché est celui que le serveur vient de RECOMPOSER.
      // ⚠️ Les champs de saisie sont vidés SEULEMENT après un succès : sur un
      // refus, l'opérateur retrouve ce qu'il avait tapé.
      setSaisie({ to: "", cc: "", bcc: "" });
      onBrouillonChange(corps);
    } catch (e) {
      setErreur(e.message);
      if (onErreur) onErreur(e.message);
    } finally { setOccupe(null); }
  }, [brouillon, adresses, saisie, textes, onBrouillonChange, onErreur]);

  const ajouterAlAnnuaire = useCallback(async () => {
    if (!nouvelle?.email || !nouvelle?.organisme) return;
    setOccupe("annuaire"); setErreur(null);
    try {
      const r = await fetch(`${BASE}/annuaire`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...nouvelle, source: "saisie_manuelle" }),
      });
      const corps = await r.json();
      if (!r.ok) { setErreur(corps.detail || `Refus (${r.status})`); return; }
      setNouvelle(null);
      await charger();
    } catch (e) { setErreur(e.message); } finally { setOccupe(null); }
  }, [nouvelle, charger]);

  if (!brouillon) return null;
  const fige = brouillon.statut && brouillon.statut !== "brouillon";

  return (
    <div style={{ marginTop: 14 }} data-testid="destinataires-panel">

      {/* ═══ LES DESTINATAIRES, EN SAISIE ═══════════════════════════════ */}
      <p style={{ ...etiquette, marginBottom: 6 }}>
        Destinataires — ce qui recevra RÉELLEMENT le pli
      </p>
      <div style={{ display: "grid", gap: 10 }}>
        {CHAMPS.map(({ cle, libelle, aide }) => (
          <div key={cle} data-testid={`destinataires-${cle}`}>
            <label style={etiquette} htmlFor={`adr-${cle}`}>
              {libelle} <span style={{ color: "var(--oh-text-muted)" }}>· {aide}</span>
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "4px 0" }}>
              {adresses[cle].map((a) => (
                <span key={a} data-testid={`adresse-${cle}-${a}`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6,
                               padding: "3px 8px", borderRadius: 8, fontSize: 12,
                               background: "var(--oh-accent-bg)",
                               color: "var(--oh-accent-text)" }}>
                  {a}
                  {!fige && (
                    <button type="button" onClick={() => retirer(cle, a)}
                            aria-label={`Retirer ${a}`}
                            style={{ background: "none", border: "none", cursor: "pointer",
                                     color: "inherit", padding: 0, lineHeight: 1 }}>
                      <X style={{ width: 12, height: 12 }} />
                    </button>
                  )}
                </span>
              ))}
              {!adresses[cle].length && (
                <span style={{ fontSize: 12, color: "var(--oh-text-muted)" }}>—</span>
              )}
            </div>
            {!fige && (
              <div style={{ display: "flex", gap: 6 }}>
                <input id={`adr-${cle}`} type="email" style={champStyle}
                       value={saisie[cle]} placeholder="adresse@exemple.gouv.fr"
                       data-testid={`saisie-${cle}`}
                       onChange={(e) => setSaisie((s) => ({ ...s, [cle]: e.target.value }))}
                       onKeyDown={(e) => {
                         if (e.key === "Enter") { e.preventDefault(); ajouter(cle, saisie[cle]); }
                       }} />
                <button type="button" onClick={() => ajouter(cle, saisie[cle])}
                        data-testid={`ajouter-${cle}`} aria-label={`Ajouter en ${libelle}`}
                        style={{ padding: "0 10px", borderRadius: 8, cursor: "pointer",
                                 border: "1px solid var(--oh-accent)",
                                 background: "transparent", color: "var(--oh-accent-text)" }}>
                  <Plus style={{ width: 14, height: 14 }} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ═══ L'ANNUAIRE — il PROPOSE, l'opérateur VALIDE ════════════════ */}
      {(fiches.length > 0 || proposition) && (
        <div style={{ marginTop: 14, padding: 12, borderRadius: 8,
                      background: "var(--oh-bg-elevated)",
                      border: "0.5px solid var(--oh-border)" }}
             data-testid="annuaire-bloc">
          <p style={{ ...etiquette, display: "flex", alignItems: "center", gap: 6 }}>
            <BookUser style={{ width: 14, height: 14 }} /> Annuaire
            {departements.length ? ` · ${departements.join(", ")}` : ""}
          </p>

          {/* ⚠️ LE MOTIF EST AFFICHÉ. Un pré-remplissage muet serait accepté
              sans être lu — et c'est une adresse d'administration. */}
          {proposition?.motif && (
            <p style={{ fontSize: 12, color: "var(--oh-text-secondary)", margin: "4px 0 8px" }}
               data-testid="annuaire-motif">
              {proposition.motif}
            </p>
          )}
          {/* 🔑 La proposition est APPLICABLE en un geste — mais c'est un GESTE.
              DEC-025 : le choix reste humain. Un pré-remplissage automatique
              serait un choix de la machine que personne n'aurait relu. */}
          {!fige && ((proposition?.to || []).length > 0
                     || (proposition?.cc || []).length > 0) && (
            <button type="button" data-testid="appliquer-proposition"
                    onClick={() => {
                      (proposition.to || []).forEach((a) => ajouter("to", a));
                      (proposition.cc || []).forEach((a) => ajouter("cc", a));
                    }}
                    style={{ marginBottom: 8, padding: "5px 10px", borderRadius: 8,
                             fontSize: 12, cursor: "pointer", background: "transparent",
                             border: "1px solid var(--oh-accent)",
                             color: "var(--oh-accent-text)" }}>
              Reprendre cette proposition
              {" "}({(proposition.to || []).length} à · {(proposition.cc || []).length} copie)
            </button>
          )}

          {proposition?.manque && (
            <p style={{ fontSize: 12, margin: "4px 0 8px", padding: 8, borderRadius: 8,
                        background: "var(--oh-stat-amber-bg)",
                        color: "var(--oh-stat-amber-text)" }}
               data-testid="annuaire-manque">
              {proposition.manque}
            </p>
          )}

          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
            {fiches.map((f) => {
              const st = TYPE_STYLE[f.type] || TYPE_STYLE.generique;
              return (
                <li key={f.email} data-testid={`fiche-${f.email}`}
                    style={{ display: "flex", flexWrap: "wrap", alignItems: "center",
                             gap: 8, fontSize: 12, color: "var(--oh-text)" }}>
                  <span style={{ padding: "2px 6px", borderRadius: 6,
                                 background: st.bg, color: st.text }}>{st.label}</span>
                  <span style={{ fontWeight: 500 }}>{f.organisme}</span>
                  {f.nom && <span style={{ color: "var(--oh-text-secondary)" }}>{f.nom}</span>}
                  <span style={{ color: "var(--oh-text-muted)" }}>{f.email}</span>
                  {!fige && CHAMPS.map(({ cle, libelle }) => (
                    <button key={cle} type="button" onClick={() => ajouter(cle, f.email)}
                            data-testid={`fiche-${f.email}-${cle}`}
                            style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11,
                                     cursor: "pointer", background: "transparent",
                                     border: "1px solid var(--oh-border)",
                                     color: "var(--oh-text-secondary)" }}>
                      {libelle}
                    </button>
                  ))}
                </li>
              );
            })}
          </ul>

          {!fige && (
            <div style={{ marginTop: 10 }}>
              {!nouvelle ? (
                <button type="button" data-testid="ouvrir-ajout-annuaire"
                        onClick={() => setNouvelle({ organisme: "", email: "",
                                                     type: "generique",
                                                     departement: departements[0] || "" })}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                 padding: "5px 10px", borderRadius: 8, fontSize: 12,
                                 cursor: "pointer", background: "transparent",
                                 border: "1px solid var(--oh-border)",
                                 color: "var(--oh-text-secondary)" }}>
                  <UserPlus style={{ width: 13, height: 13 }} /> Ajouter à l'annuaire
                </button>
              ) : (
                <div style={{ display: "grid", gap: 6 }} data-testid="formulaire-annuaire">
                  <input style={champStyle} placeholder="Organisme (ex. DDT 60)"
                         data-testid="annuaire-organisme" value={nouvelle.organisme}
                         onChange={(e) => setNouvelle((n) => ({ ...n, organisme: e.target.value }))} />
                  <input style={champStyle} type="email" placeholder="adresse@exemple.gouv.fr"
                         data-testid="annuaire-email" value={nouvelle.email}
                         onChange={(e) => setNouvelle((n) => ({ ...n, email: e.target.value }))} />
                  <input style={champStyle} placeholder="Nom du contact (facultatif)"
                         data-testid="annuaire-nom" value={nouvelle.nom || ""}
                         onChange={(e) => setNouvelle((n) => ({ ...n, nom: e.target.value }))} />
                  <div style={{ display: "flex", gap: 10, fontSize: 12,
                                color: "var(--oh-text-secondary)" }}>
                    {/* 🔑 Le TYPE porte la règle métier : un dépôt part vers le
                        SERVICE, une réponse à un complément vers l'AGENT. */}
                    {["generique", "nominatif"].map((t) => (
                      <label key={t} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <input type="radio" name="type-annuaire" value={t}
                               data-testid={`annuaire-type-${t}`}
                               checked={nouvelle.type === t}
                               onChange={() => setNouvelle((n) => ({ ...n, type: t }))} />
                        {t === "generique" ? "adresse du service" : "contact nominatif"}
                      </label>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" onClick={ajouterAlAnnuaire}
                            disabled={occupe === "annuaire"}
                            data-testid="valider-ajout-annuaire"
                            style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12,
                                     cursor: "pointer", border: "none",
                                     background: "var(--oh-accent-solid)",
                                     color: "var(--oh-accent-contrast)" }}>
                      Enregistrer la fiche
                    </button>
                    <button type="button" onClick={() => setNouvelle(null)}
                            style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12,
                                     cursor: "pointer", background: "transparent",
                                     border: "1px solid var(--oh-border)",
                                     color: "var(--oh-text-secondary)" }}>
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ LE TEXTE COMPLÉMENTAIRE, PAR DEMANDE ══════════════════════ */}
      {demandes.length > 0 && (
        <div style={{ marginTop: 14 }} data-testid="textes-complementaires">
          <p style={etiquette}>
            Texte complémentaire — inséré AVANT « Ce pli porte sur »
          </p>
          <p style={{ fontSize: 12, color: "var(--oh-text-muted)", margin: "2px 0 8px" }}>
            {/* ⚠️ On dit ce que le texte NE PEUT PAS faire : il ajoute, il ne
                remplace pas l'annonce du périmètre. */}
            Il s'ajoute au message. ⛔ Il ne remplace pas l'annonce du périmètre,
            qui reste dérivée de ce qui a été consigné.
          </p>
          <div style={{ display: "grid", gap: 10 }}>
            {demandes.map((d) => (
              <div key={d.code}>
                <label style={{ ...etiquette, display: "block" }} htmlFor={`txt-${d.code}`}>
                  {d.libelle_sens || d.sens} · {(d.territoires || []).join(", ") || d.code}
                </label>
                <textarea id={`txt-${d.code}`} rows={4} disabled={fige}
                          data-testid={`texte-${d.code}`}
                          value={textes[d.code] || ""}
                          placeholder="Facultatif — votre propos, dans vos mots."
                          onChange={(e) => setTextes((t) => ({ ...t, [d.code]: e.target.value }))}
                          style={{ ...champStyle, height: "auto", padding: 8,
                                   lineHeight: 1.5, resize: "vertical" }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {erreur && (
        <div style={{ marginTop: 10, padding: 8, borderRadius: 8, fontSize: 12,
                      background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)" }}
             data-testid="destinataires-erreur">
          {typeof erreur === "string" ? erreur : JSON.stringify(erreur)}
        </div>
      )}

      {!fige && (
        <button type="button" onClick={enregistrer} disabled={occupe === "enregistrer"}
                data-testid="enregistrer-destinataires"
                style={{ marginTop: 12, padding: "7px 14px", borderRadius: 8, fontSize: 13,
                         cursor: "pointer", border: "none", display: "inline-flex",
                         alignItems: "center", gap: 6,
                         background: "var(--oh-accent-solid)",
                         color: "var(--oh-accent-contrast)" }}>
          {occupe === "enregistrer" && (
            <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
          )}
          Enregistrer et recomposer le message
        </button>
      )}
    </div>
  );
}
